import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';

const PAYMENT_COLORS: Record<string, string> = {
  Cashfree: '#6933d3',
  Razorpay: '#4ECDC4',
  Stripe: '#45B7D1',
  PayPal: '#96CEB4',
  'Account Balance': '#f97316',
  Binance: '#facc15',
  'Coin Flex Pay': '#fb7185',
  'INR Portal': '#2dd4bf',
  Manual: '#64748b',
  Unknown: '#9ca3af',
};

const ChartPaymentMethods: React.FC = () => {
  const { transactions, selectedFilter, isCustomRangePending } =
    useAnalyticsContext();

  const paymentTotals =
    transactions.data?.summary?.paymentMethodTotals ?? {};

  const chartData = useMemo(() => {
    return Object.entries(paymentTotals)
      .map(([method, stats]) => ({
        name: method,
        value: Number(stats?.amount || 0),
        transactions: Number(stats?.transactions || 0),
      }))
      .filter(item => item.value > 0 || item.transactions > 0)
      .sort((a, b) => b.value - a.value);
  }, [paymentTotals]);

  const totalAmount = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card className="border-none h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">
          Payment Method Mix
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {transactions.isLoading ? (
          <div className="flex h-[260px] items-center justify-center">
            <div className="h-48 w-48 animate-pulse rounded-full bg-muted/50" />
          </div>
        ) : isCustomRangePending ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Select a start and end date to view custom range insights.
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            No payment activity for the selected period.
          </div>
        ) : (
          <>
            <div className="h-[220px]">
              <ResponsiveContainer>
                <PieChart>
                  <Tooltip
                    formatter={(value: number, name: string, entry: any) => [
                      `${value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`,
                      `${name} · ${entry.payload.transactions} txns`,
                    ]}
                  />
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {chartData.map(entry => (
                      <Cell
                        key={entry.name}
                        fill={
                          PAYMENT_COLORS[entry.name] || PAYMENT_COLORS.Unknown
                        }
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid gap-2 text-sm">
              {chartData.map(entry => {
                const percentage =
                  totalAmount > 0 ? (entry.value / totalAmount) * 100 : 0;
                return (
                  <div
                    key={entry.name}
                    className="flex items-center justify-between rounded-lg border bg-card/50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor:
                            PAYMENT_COLORS[entry.name] ||
                            PAYMENT_COLORS.Unknown,
                        }}
                      />
                      <span className="font-medium text-foreground">
                        {entry.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <span>{entry.transactions} txns</span>
                      <span>
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {selectedFilter !== 'custom' && (
          <p className="text-xs text-muted-foreground">
            Based on paid transactions for the selected period. Totals converted
            to the default reporting currency.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ChartPaymentMethods;

